import React, { Component } from 'react';
import { appContext } from '../../../../AppContext';
import IbssbreadcrumbLeft from '../../../../Components/Navigation/Breadcrumbs/IbssbreadcrumbLeft';
import SearchCriteriaHeatmaps, { IQuery } from './SearchCriteriaHeatmaps';
import Alert from '../../../../Components/Miscellaneous/Alert/Alert';
import Helper, { getPeriodTypeString, periodType } from '../../../../Common/Helper';
import Spinner from '../../../../Components/Navigation/LoadingSpinner/Spinner';
import FloorPlan, { IFloorPlanSpace as IFloorPlanSpace, IMapUrl, IPagedFloorPlanSpaces } from '../../../../Components/Data/FloorPlan/FloorPlan';
import { DateTime } from 'luxon';
import { ISpaceUtilisationSummary } from '../../../../Providers.Api/SpaceUtilisationSummaries/GetManyByPeriodEndpoint';
import { DateHelper } from '../../../../Common/DateHelper';
import Guid from '../../../../Common/Guid';
import IbssDrawer from '../../../../Components/Drawer/IbssDrawer';
import { IFloor } from '../../../../Providers.Api/Models';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { QueryParams } from './QueryParams';
import { StringHelper } from "../../../../Common/StringHelper";
import { Icons } from '../../../../Common/AllsvgIcons';


class ViewSpaceUtilisation extends IbssPage<IProps, IState, QueryParams>
{

    private labels = appContext().labels;
    private spaces = new Array<ISpaceUtilisationSummary>();
    private appState = appContext().state;
    private apiClient = appContext().apiClient;
    private localStorageProvider = appContext().localStorageProvider;
    private cachedFloors: IFloor[] = [];
    private string = new StringHelper();
    private pageCacheKey = 'b739a8f4-5d3c-4e8a-9b2d-7c6f1e2d9c3a';

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.cachedFloors = appContext().localStorageProvider.getNodeData().Regions.flatMap(region => region.Buildings).flatMap(building => building.Floors);
        const mapUrls = this.cachedFloors.map(i => ({ floorId: i.Node_Id, url: i.Floor_MapURI }));

        this.state = {
            floorId: 0,
            isOpen: false,
            mapUrls: mapUrls,
            isLoading: false,
            searchResults: SearchResults.Map,
            mapSpaces: [],
            loadMap: Guid.empty,
            heatMapBreadCrumbs: [],
            searchData: ["building", "floor", "class", "spaceType", "date", "periodType",],
            selectedBuildingId: this.appState.buildingId,
            buildingName: '',
            floorName: '',
            periodStartDate: DateHelper.now(),
            periodType: 0,
        };
    };

    public async queryParamsDidUpdate(firstLoad: boolean, prevParams: QueryParams): Promise<void>
    {
        this.pageTitle = this.labels.HublabelSpaceAnalyticsHeatmaps;
        const queryParams = this.queryParams;

        if (firstLoad)
        {
            this.clearCache();
        }
        
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }

        const rootNode = this.localStorageProvider.getNodeData();
        const buildings = rootNode.Regions.flatMap(i => i.Buildings);
        const defaultBuilding = buildings.find(i => i.Node_Id == queryParams.building) ?? rootNode;
        const buildingName = ('Name' in defaultBuilding ? defaultBuilding.Name : defaultBuilding.Node_Name);

        const floors = Helper.getFloorsByBuildingId(queryParams.building ?? 1);
        const defaultFloor: (IFloor | undefined) = floors[0];

        if (!queryParams.floor || prevParams.building !== queryParams.building)
        {
            return this.pushQueryParams({ floor: defaultFloor?.Node_Id ?? 1 }, true);
        }

        if (!queryParams.period || !queryParams.date)
        {
            const date = DateHelper.now(queryParams.building);
            const defaultMonthStartDate = date.minus({ months: 1 }).startOf('month');

            this.pushQueryParams({
                period: this.string.toKebabCase(this.labels.HublabelMonth),
                date: defaultMonthStartDate,
            }, true);
            return;
        }

        const floorId = queryParams.floor;
        const floorName = floors.find(f => f.Node_Id === floorId)?.Node_Name ?? defaultFloor.Node_Name;
        const period = periodType.find(i => i.Name == queryParams.period)?.Value ?? 1;

        await this.setStateAsync({
            selectedBuildingId: queryParams.building ?? 1,
            buildingName: buildingName,
            floorId: floorId,
            floorName: floorName,
            periodStartDate: queryParams.date,
            periodType: period,
        });

        this.fetchData()
    }

    private clearCache(): void
    {
        appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey + '_'));
    }

    public async fetchData() 
    {
        const queryData = {
            buildingId: this.state.selectedBuildingId,
            buildingName: this.state.buildingName,
            floorId: this.state.floorId,
            floorName: this.state.floorName,
            periodStartDate: this.state.periodStartDate,
            periodEndDate: this.state.periodStartDate.plusPeriods(this.state.periodType),
            periodType: this.state.periodType,
        };

        this.setState({ isLoading: true });

        try
        {
            const buildingId = this.state.selectedBuildingId;
            const startDate = queryData.periodStartDate.toUtcByNode(buildingId).startOf('day');
            const endDate = startDate.plusPeriods(queryData.periodType);

            const endpoint = {
                name: 'spaceUtilisationSummaries.getManyByPeriod',
                buildingId: buildingId,
                periodType: queryData.periodType,
                startDate: startDate,
                endDate: endDate,
                floorNodeId: queryData.floorId.toString(),
            };

            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => this.apiClient.spaceUtilisationSummaries.getManyByPeriod(
                    endpoint.buildingId,
                    endpoint.periodType,
                    endpoint.startDate,
                    endpoint.endDate,
                    endpoint.floorNodeId
                )
            );

            this.spaces = response;
            const heatMapBreadCrumbs = this.makeHeatMapCriteria();
            this.updateSearchResults(heatMapBreadCrumbs);
            this.setState({ heatMapBreadCrumbs, isLoading: false });
        }
        catch
        {
            this.setState({ isLoading: false });
        }
    }
    public toggleDrawer(open: boolean)
    {
        this.setState({
            isOpen: open,
        });
    }

    public mapFailedToLoad(): void
    {
        this.setState({
            isLoading: false,
            searchResults: SearchResults.MapFailedToLoad,
        });
    };


    public async updateSearchResults(heatMapBreadCrumbs: any)
    {

        this.setState({ isLoading: true });

        let searchResults!: SearchResults;
        let floorId: number;
        const floorIds = this.spaces.map(i => i.Node_Id).distinct();
        const floors = this.cachedFloors.filter(i => floorIds.contains(i.Node_Id));

        let mapSpaceIds = new Array<IFloorPlanSpace>();

        if (this.spaces.length === 0)
        {
            floorId = 0;
            searchResults = SearchResults.NoResults;
        }
        else if (floors.length !== 1)
        {
            floorId = 0;
            searchResults = SearchResults.MapTooManyFloors;
        }
        else
        {
            searchResults = SearchResults.Map;
            floorId = floors[0].Node_Id;
            mapSpaceIds = this.spaces.map(i => ({ id: i.Space_Id, colour: this.getColorFromRedToGreenByPercentage(i.Period_Current_Space_Value), getColourFromData: false, periodCurrentSpaceValue: i.Period_Current_Space_Value }));
        }

        this.setState({
            isOpen: false,
            searchResults: searchResults,
            mapSpaces: mapSpaceIds,
            floorId: floorId,
            loadMap: Guid.new(),
            isLoading: false,
            heatMapBreadCrumbs: heatMapBreadCrumbs,
        });
    }

    private floorPlanSpacesRequested(): Promise<IPagedFloorPlanSpaces>
    {
        const spaces = {
            skipToken: "",
            spaces: this.state.mapSpaces,
        };
        return Promise.resolve(spaces);
    }

    public makeHeatMapCriteria()
    {
        let arr: any = []

        this.state.searchData.map((option: string) =>
        {

            if (option === 'building')
            {
                if (this.state.selectedBuildingId !== undefined)
                {
                    arr.push({
                        src: Icons.building,
                        value: this.state.buildingName,
                    });
                }
            }
            if (option === 'floor')
            {
                if (this.state.floorId !== undefined)
                {
                    arr.push({
                        src: Icons.FloorIcon,
                        value: this.state.floorName,
                    });

                }
            }
            if (option === "class")
            {
                arr.push({
                    src: Icons.desk,
                    value: "Any",
                });
            }
            if (option === "spaceType")
            {
                arr.push({
                    src: Icons.desk,
                    value: "Any",
                });
            }
            if (option === 'date')
            {
                if (this.state.periodStartDate !== undefined)
                {
                    const periodStartDate = this.state.periodStartDate.toFormat('dd/MM/yy');
                    arr.push({
                        src: Icons.calenderIcon,
                        value: periodStartDate,
                    });
                }
            }
            if (option === "periodType")
            {
                if (this.state.periodType != undefined)
                {
                    const period = getPeriodTypeString(this.state.periodType);
                    arr.push({
                        src:  Icons.calenderIcon,
                        value: period,
                    });
                }
            }
        })

        return arr;
    };

    public getColorFromRedToGreenByPercentage(value: number)
    {
        const hue = ((1 - (value / 100)) * 120).toString(10);
        return ["hsl(", hue, ",100%,50%)"].join("");
    };

    private handleSearchResultsChange(query: IQuery): void
    {
        const period = periodType.find(i => i.Value === query.periodType)?.Name ?? 'month';
        const date = DateHelper.fromIsoOrJsDate(query.periodStartDate).startOf('day');
        this.pushQueryParams({
            floor: parseInt(`${query.floorNodeId}`),
            period: period,
            date: date
        })
    }

    public render(): JSX.Element
    {
        return (
            <div>
                {this.state.isLoading && <Spinner />}
                <div className="page-height-exct-header">
                    <div className="rightPanel-main-content" style={{ paddingBottom: "5px" }}>
                        <div className="space-box-cont">
                            {/* Breadcrumb chip search filter */}
                            <div className="left-space-box-cont flex-row-bredcrumb">
                                <IbssbreadcrumbLeft value={this.state.heatMapBreadCrumbs} />
                            </div>
                            <div className="right-space-box-cont">
                                <div>
                                    <button type="button" className="edit-search btn-primary btn-md" onClick={() => this.toggleDrawer(true)}>{this.labels.HubButtonEditsearchcriteria}</button>
                                </div>
                            </div>
                            <IbssDrawer
                                anchor='right'
                                open={this.state.isOpen}
                                onClose={() => this.toggleDrawer(false)}
                                className="flex-search-filter-criteria"
                            >
                                <SearchCriteriaHeatmaps
                                    toggleDrawer={isOpen => this.toggleDrawer(isOpen)}
                                    buildingId={this.state.selectedBuildingId}
                                    floorId={this.state.floorId}
                                    periodType={this.state.periodType}
                                    periodStartDate={this.state.periodStartDate}
                                    periodEndDate={this.state.periodStartDate.plusPeriods(this.state.periodType)} 
                                    updateSearchResults={query => this.handleSearchResultsChange(query)}
                                />
                            </IbssDrawer>
                        </div>
                        <div className={"space-analytics-page-layout " + this.resultsCssClass}>
                            {this.renderResults()}
                        </div>
                    </div>
                </div>
            </div>
        )
    };

    private renderResults(): JSX.Element
    {
        switch (this.state.searchResults)
        {
            case SearchResults.NoResults:
                return (!this.state.isLoading ? <Alert key="noResults" title={this.labels.HubLabelNoSpacesAvailable} text={this.labels.HubLabelsearchCriteriaNoSpaces} /> : <></>);

            case SearchResults.MapTooManyFloors:
                return (<Alert key="mapTooManyFloors" title={this.labels.HubLabelmapView} text={this.labels.HubLabelSelectAFloor} />);

            case SearchResults.MapFailedToLoad:
                return (<Alert key="mapFailedToLoad" title={this.labels.HubmapFailedToLoad} text={this.labels.HubLabelUsingTheListView} />);

            case SearchResults.Map:
                return (<FloorPlan
                    key="map"
                    mapUrls={this.state.mapUrls}
                    loadSpaces={this.state.loadMap}
                    onRequestSpaces={() => this.floorPlanSpacesRequested()}
                    mapFailedToLoad={() => this.mapFailedToLoad()}
                    floorId={this.state.floorId}
                    enableSpaceClick={false}
                    isHeatmapLayout={true}
                />);

            default:
                return (<></>);
        };
    };

    private get resultsCssClass(): string
    {
        switch (this.state.searchResults)
        {
            case SearchResults.NoResults:
            case SearchResults.MapTooManyFloors:
            case SearchResults.MapFailedToLoad:
                return "search-results-cont--alert";

            case SearchResults.Map:
                return "search-results-cont--map";

            default:
                return "";
        };
    };

};

export default ViewSpaceUtilisation;

export interface IProps
{

}
export interface IState
{
    floorId: number;
    isOpen: boolean;
    mapUrls: IMapUrl[];
    isLoading: boolean;
    searchResults: SearchResults;
    mapSpaces: Array<IFloorPlanSpace>;
    loadMap: Guid;
    heatMapBreadCrumbs: Array<IBreadCrumb>;
    searchData: Array<string>;
    selectedBuildingId: number;
    buildingName: string,
    floorName: string,
    periodStartDate: DateTime,
    periodType: number,
}

export interface IBreadCrumb
{
    src: string;
    value: string;
}

export enum SearchResults
{
    NoResults,
    MapTooManyFloors,
    MapFailedToLoad,
    Map,
}